import { useRef, useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import InvoiceGenerator from '../components/Invoice/InvoiceGenerator';
import { t } from '../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

export default function InvoiceAddPage() {
  const [searchParams] = useSearchParams();
  const { id: invoiceId } = useParams();
  const dnoteId = searchParams.get('dnote_id');
  const invoiceGeneratorRef = useRef();
  
  // Local state for checkboxes to ensure they update
  const [settings, setSettings] = useState({
    reklamacijeEnabled: true,
    footerEnabled: true,
    IspratnicaEnabled: true,
    PodignalEnabled: true,
    voziloEnabled: true
  });

  // Local state for view mode
  const [viewMode, setViewMode] = useState('advanced');

  // State for delivery note info (for breadcrumb)
  const [deliveryNoteInfo, setDeliveryNoteInfo] = useState(null);

  // State for existing invoice data (for edit mode)
  const [existingInvoiceData, setExistingInvoiceData] = useState(null);

  // Fetch delivery note info for breadcrumb when dnote_id is present
  useEffect(() => {
    const fetchDeliveryNoteInfo = async () => {
      if (!dnoteId) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${apiUrl}/dnote-view/${dnoteId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDeliveryNoteInfo(data.deliveryNote);
        }
      } catch (error) {
        console.error('Failed to fetch delivery note info for breadcrumb:', error);
      }
    };

    fetchDeliveryNoteInfo();
  }, [dnoteId]);

  // Fetch existing invoice data for edit mode
  useEffect(() => {
    const fetchExistingInvoiceData = async () => {
      if (!invoiceId) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${apiUrl}/invoice-view/${invoiceId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setExistingInvoiceData(data);
        }
      } catch (error) {
        console.error('Failed to fetch existing invoice data:', error);
      }
    };

    fetchExistingInvoiceData();
  }, [invoiceId]);

  const handlePrint = () => {
    if (invoiceGeneratorRef.current) {
      invoiceGeneratorRef.current.handlePrint();
    }
  };

  const handleSave = () => {
    if (invoiceGeneratorRef.current) {
      invoiceGeneratorRef.current.handleSave();
    }
  };

  const handleSaveDraft = () => {
    if (invoiceGeneratorRef.current) {
      invoiceGeneratorRef.current.handleSaveDraft();
    }
  };

  const handleAddRow = () => {
    if (invoiceGeneratorRef.current) {
      invoiceGeneratorRef.current.handleAddRow();
    }
  };

  const handleToggleSetting = (settingName) => {
    setSettings(prev => ({
      ...prev,
      [settingName]: !prev[settingName]
    }));
    
    // Also call the function on the generator
    if (invoiceGeneratorRef.current) {
      switch (settingName) {
        case 'reklamacijeEnabled':
          invoiceGeneratorRef.current.toggleReklamacije();
          break;
        case 'footerEnabled':
          invoiceGeneratorRef.current.toggleFooter();
          break;
        case 'IspratnicaEnabled':
          invoiceGeneratorRef.current.toggleIspratnicaEnabled();
          break;
        case 'PodignalEnabled':
          invoiceGeneratorRef.current.togglePodignalEnabled();
          break;
        case 'voziloEnabled':
          invoiceGeneratorRef.current.toggleVoziloEnabled();
          break;
      }
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (invoiceGeneratorRef.current) {
      invoiceGeneratorRef.current.setViewMode(mode);
    }
  };



  return (
    <div className="min-h-screen bg-gray-100 -m-8 p-8">
      <div className="flex w-full gap-6">
        {/* Main content area */}
        <div className="flex-1 p-4 bg-gray-200 rounded-lg shadow">
          <InvoiceGenerator
            ref={invoiceGeneratorRef} 
            hideSidebar={true} 
            existingInvoiceData={existingInvoiceData}
            isEdit={!!invoiceId}
          />
        </div>

      {/* Action buttons sidebar - outside of invoice */}
      <div className="w-48 p-4 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col gap-3 h-fit">
        {/* Advanced Mode Toggle */}
        <div className="mb-2">
          <div className="text-xs text-gray-600 mb-1 font-medium text-center">{t('Напреден режим')}</div>
          <div className="flex bg-gray-200 rounded-lg p-1 shadow-sm border">
            <button
              onClick={() => handleViewModeChange('basic')}
              className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'basic'
                  ? 'bg-red-500 text-white shadow-sm'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t('ИСКЛ')}
            </button>
            <button
              onClick={() => handleViewModeChange('advanced')}
              className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'advanced'
                  ? 'bg-green-500 text-white shadow-sm'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t('ВКЛ')}
            </button>
          </div>
        </div>

        <hr className="my-2 border-gray-200" />

        <button
          onClick={handlePrint}
          className="w-full bg-white border border-gray-300 text-gray-800 py-2 px-4 rounded hover:bg-gray-100 text-sm font-medium"
        >
          {t('Печати')}
        </button>
        
        {/* <button
          onClick={handleSaveDraft}
          className="w-full bg-yellow-100 text-yellow-900 py-2 px-4 rounded hover:bg-yellow-200 text-sm font-medium"
        >
          Save Draft
        </button> */}
        
        <button
          onClick={handleSave}
          className="w-full bg-orange-600 text-white py-2 px-4 rounded hover:bg-orange-700 text-sm font-medium"
        >
          {t('Зачувај')}
        </button>

        <hr className="my-2 border-gray-200" />
        
        <div className="text-sm font-semibold text-gray-700 mb-2">{t('Подесувања')}</div>
        
        <button
          onClick={handleAddRow}
          className="w-full bg-gray-200 text-gray-800 text-sm py-2 px-4 rounded hover:bg-gray-300 font-medium"
        >
          {t('+ Додади ред')}
        </button>
        
        {viewMode === 'advanced' && (
          <>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleToggleSetting('reklamacijeEnabled')}
                className={`w-full text-sm py-1 px-2 rounded bg-white text-gray-800 hover:bg-gray-100 border text-left ${
                  settings.reklamacijeEnabled ? 'border-green-600' : 'border-red-300'
                }`}
              >
                {settings.reklamacijeEnabled ? t('Рекламации вкл.') : t('Рекламации искл.')}
              </button>

              <button
                onClick={() => handleToggleSetting('footerEnabled')}
                className={`w-full text-sm py-1 px-2 rounded bg-white text-gray-800 hover:bg-gray-100 border text-left ${
                  settings.footerEnabled ? 'border-green-600' : 'border-red-300'
                }`}
              >
                {settings.footerEnabled ? t('Подножје вкл.') : t('Подножје искл.')}
              </button>
            </div>

            <div className="text-xs font-semibold text-gray-600 mb-2 mt-3">{t('Прикажи полиња')}</div>
            <div className="space-y-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={settings.IspratnicaEnabled}
              onChange={() => handleToggleSetting('IspratnicaEnabled')}
              className={`form-checkbox h-4 w-4 text-green-600 border ${
                settings.IspratnicaEnabled ? 'border-green-600' : 'border-red-300'
              }`}
            />
            <label className="ml-2 text-sm text-gray-800">{t('Испратница бр.')}</label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={settings.PodignalEnabled}
              onChange={() => handleToggleSetting('PodignalEnabled')}
              className={`form-checkbox h-4 w-4 text-green-600 border ${
                settings.PodignalEnabled ? 'border-green-600' : 'border-red-300'
              }`}
            />
            <label className="ml-2 text-sm text-gray-800">{t('Подигнал')}</label>
          </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.voziloEnabled}
                  onChange={() => handleToggleSetting('voziloEnabled')}
                  className={`form-checkbox h-4 w-4 text-green-600 border ${
                    settings.voziloEnabled ? 'border-green-600' : 'border-red-300'
                  }`}
                />
                <label className="ml-2 text-sm text-gray-800">{t('Возило')}</label>
              </div>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
